import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { BROWSE_SORTS } from 'fym-shared/src/music24/browseSearch';
import { TypedMemo } from '../../../common/types';
import { MobileSheet } from '../../navigation/mobile/MobileSheet';
import { SMLButton } from '../../components/primitives/SMLButton';
import { SMLCountrySelector } from '../../components/SMLCountrySelector/SMLCountrySelector';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useTRPCAPI, useTRPCQuery } from '../../../../trpc/trpc';
import type { BrowseUrlPatch, BrowseUrlState } from '../hooks/useBrowseUrlState';
import { CLEARED_FILTERS } from '../search/BrowseFilters';
import { BROWSE_SORT_LABELS } from '../search/BrowseSortSelect';
import { BrowseLockedFilter } from '../search/BrowseLockedFilter';
import { BrowseServiceSelect } from '../search/BrowseServiceSelect';
import { browseMessages as messages } from '../browseMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowsePhone.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** Genre chips in the sheet (design c2: "Any", five genres, more in the desktop popover). */
const SHEET_GENRES = 8;

interface BrowseFilterSheetProps {
  state: BrowseUrlState;
  update: (patch: BrowseUrlPatch) => void;
  resultCount: number | null;
  onClose: () => void;
}

/** The filter sheet of a phone (design c2). A change applies at once; the button shows the new count. */
const BrowseFilterSheetComponent: React.FC<BrowseFilterSheetProps> = ({ state, update, resultCount, onClose }) => {
  const intl = useIntl();
  const capabilities = useMusic24Capabilities();
  const trpc = useTRPCAPI();
  const genres = useTRPCQuery(trpc.browse.genres.queryOptions(undefined, { staleTime: 60 * 60 * 1000 }));
  const genreOptions = (genres.data?.genres ?? []).slice(0, SHEET_GENRES);

  return (
    <MobileSheet
      id="browse-filter-sheet"
      title={intl.formatMessage(messages.filters)}
      aside={
        <button type="button" className={styles.chip} onClick={() => update(CLEARED_FILTERS)}>
          {intl.formatMessage(messages.clearFilters)}
        </button>
      }
      onClose={onClose}
    >
      <div className={styles.sheetBody}>
        <div className={styles.sheetGroup}>
          <span className={styles.sheetLabel}>{intl.formatMessage(messages.genreLabel)}</span>
          <div className={styles.chips}>
            <button
              type="button"
              className={clsx(styles.chip, state.genre === undefined && styles.chipOn)}
              onClick={() => update({ genre: undefined })}
            >
              {intl.formatMessage(messages.anyValue)}
            </button>
            {genreOptions.map((genre) => (
              <button
                key={genre}
                type="button"
                className={clsx(styles.chip, state.genre === genre && styles.chipOn)}
                onClick={() => update({ genre })}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.sheetGroup} role="radiogroup" aria-label={intl.formatMessage(messages.sortLabel)}>
          <span className={styles.sheetLabel}>{intl.formatMessage(messages.sortLabel)}</span>
          {BROWSE_SORTS.map((sort) => (
            <button
              key={sort}
              type="button"
              role="radio"
              aria-checked={state.sort === sort}
              className={clsx(styles.option, state.sort === sort && styles.optionOn)}
              onClick={() => update({ sort: sort === 'match' ? undefined : sort })}
            >
              {intl.formatMessage(BROWSE_SORT_LABELS[sort])}
              <span className={styles.radio} aria-hidden="true" />
            </button>
          ))}
        </div>
        <div className={styles.chips}>
          {capabilities.canFilterGeographically ? (
            <SMLCountrySelector />
          ) : (
            <BrowseLockedFilter
              label={intl.formatMessage(messages.countryLabel)}
              value={intl.formatMessage(messages.globalValue)}
              feature={SMLUpgradeFeature.geography}
            />
          )}
          {capabilities.canFilterByMusicService ? (
            <BrowseServiceSelect value={state.service} onChange={(service) => update({ service })} />
          ) : (
            <BrowseLockedFilter
              label={intl.formatMessage(messages.serviceLabel)}
              value={intl.formatMessage(messages.serviceAll)}
              feature={SMLUpgradeFeature.musicServiceFilter}
            />
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={state.hideFollowed}
          className={clsx(styles.option, state.hideFollowed && styles.optionOn)}
          onClick={() => update({ hideFollowed: state.hideFollowed ? undefined : '1' })}
        >
          {intl.formatMessage(messages.hideFollowed)}
          <span className={styles.radio} aria-hidden="true" />
        </button>
        <SMLButton variant="primary" className={styles.primary} onClick={onClose}>
          {resultCount === null
            ? intl.formatMessage(messages.filters)
            : intl.formatMessage(messages.showResults, { count: resultCount })}
        </SMLButton>
      </div>
    </MobileSheet>
  );
};

export const BrowseFilterSheet = TypedMemo(BrowseFilterSheetComponent);
